import axios from "axios";
import { sendBatchPayout, PayPalPayoutItem } from "./paypal.service";
import { adminApi, api } from "../../lib/axios";

// Types
export type AdminRole = "ADMIN" | "SUPER_ADMIN" | "SALES";

export interface AdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: AdminRole;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AdminPayout {
  id: string;
  amount: number;
  status: "PENDING" | "COMPLETED" | "FAILED";
  recipient: string;
  recipientType: "AFFILIATE" | "BRAND";
  createdAt: string;
  completedAt?: string;
}

export interface StatWithChange {
  current: number;
  previous: number;
  percentageChange: number;
}

export interface DailyStat {
  affiliatesJoined: number;
  brandsJoined: number;
  sales: {
    count: number;
    amount: number;
  };
}

export interface DashboardStats {
  timeframe: string;
  period: {
    start: string;
    end: string;
  };
  metrics: {
    totalBrands: number;
    activeBrands: number;
    totalSales: number;
    averageSaleValue: number;
    totalSalesAmount: number;
  };
  // Keep backward compatibility for existing code
  totalBrands?: StatWithChange;
  activeBrands?: StatWithChange;
  totalAffiliates?: StatWithChange;
  activeAffiliates?: StatWithChange;
  totalRevenue?: StatWithChange;
  totalCommissions?: StatWithChange;
  dailyStats?: Record<string, DailyStat>;
}

export type AdminMrrShopStatus =
  | "billing"
  | "trial"
  | "test"
  | "free"
  | "mismatch"
  | "unreachable"
  | "uninstalled";

export interface AdminMrrShopRow {
  sessionId: string;
  shop: string;
  companyName: string | null;
  /** Brand login email, falling back to the Shopify account email. */
  email: string | null;
  /** BrandDetail id; null when the shop installed but never signed up. */
  brandDetailId: string | null;
  status: AdminMrrShopStatus;
  /** One sentence an admin can act on. */
  reason: string | null;
  /** Raw error text behind `reason` for unreachable shops. */
  detail: string | null;
  dbPlan: string | null;
  dbAmpedPlan: string | null;
  dbMrr: number;
  shopifyPlan: string | null;
  shopifyStatus: string | null;
  shopifyMrr: number;
  shopifyIsTest: boolean;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  usageThisPeriod: number;
}

/** DB-flagged subscriptions reconciled against each shop's live Shopify billing. */
export interface AdminMrrResponse {
  computedAt: string;
  fromCache: boolean;
  totals: {
    liveMrr: number;
    dbMrr: number;
    trialMrr: number;
    testMrr: number;
    usageThisPeriod: number;
    arrRunRate: number;
  };
  /** One count per bucket; the buckets add up to `totalBrands`. */
  counts: Record<AdminMrrShopStatus, number> & {
    shopsChecked: number;
    totalBrands: number;
  };
  mismatches: AdminMrrShopRow[];
  unreachable: AdminMrrShopRow[];
  shops: AdminMrrShopRow[];
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface Affiliate {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  profileImage: string | null;
}

export interface Company {
  id: string;
  companyName: string;
  description: string;
  profileImage: string;
  brandBannerImage: string;
  brandProfileImage: string;
}

export interface ProcessingPayout {
  id: string;
  amount: number;
  status: "PROCESSING" | "COMPLETED" | "FAILED";
  createdAt: string;
  updatedAt: string;
  affiliateId: string;
  paymentMethod: "PAYPAL" | "BANK_TRANSFER";
  code: string;
  paymentDetails: Record<string, unknown>;
  affiliate: Affiliate;
  company: Company;
}

export interface ProcessingPayoutsResponse {
  data: ProcessingPayout[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface AdminAuthResponse {
  id: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role: AdminRole;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  accessToken: string;
  refreshToken: string;
}

interface PayPalBatchItem {
  payout_item_id: string;
  transaction_id: string;
  transaction_status: string;
  payout_item_fee: {
    currency: string;
    value: string;
  };
  payout_item: {
    recipient_type: string;
    amount: {
      currency: string;
      value: string;
    };
    note: string;
    receiver: string;
    sender_item_id: string;
  };
}

interface PayPalResponse {
  batch_header: {
    payout_batch_id: string;
    batch_status: string;
  };
  items: PayPalBatchItem[];
}

export interface PayPalBatchPayoutResponse {
  batchId: string;
  status: string;
  payoutItems: {
    payoutItemId: string;
    transactionId: string;
    transactionStatus: string;
    payoutItemFee: {
      currency: string;
      value: string;
    };
    payoutBatchId: string;
    payoutItem: {
      recipientType: string;
      amount: {
        currency: string;
        value: string;
      };
      note: string;
      receiver: string;
      senderItemId: string;
    };
  }[];
}

export interface SupportTicket {
  id: string;
  description: string;
  mediaLinks: string[];
  createdAt: string;
  updatedAt: string;
  userType: "BRAND" | "AFFILIATE";
  userId: string;
}

export interface SupportTicketsResponse {
  data: SupportTicket[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// Types for monthly growth analytics
export interface DailyStats {
  day: number;
  brands: number;
  affiliates: number;
  orders: number;
  revenue: number;
}

export interface MonthlyTotals {
  totalBrands: number;
  totalAffiliates: number;
  totalOrders: number;
  totalRevenue: number;
}

export interface MonthlyGrowthResponse {
  year: number;
  month: number;
  dailyStats: DailyStats[];
  monthlyTotals: MonthlyTotals;
}

export interface SalesUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: AdminRole;
  isActive: boolean;
  mustChangePassword: boolean;
  passwordExpiresAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSalesUserInput {
  firstName: string;
  lastName: string;
  email: string;
}

export interface UpdateSalesUserInput {
  firstName?: string;
  lastName?: string;
  email?: string;
  isActive?: boolean;
}

export interface LoginResponse {
  mustChangePassword?: boolean;
  resetEmailSent?: boolean;
  id?: string;
  email?: string;
  password?: string;
  firstName?: string;
  lastName?: string;
  role?: AdminRole;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  accessToken?: string;
  refreshToken?: string;
}

// Service class
export class AdminService {
  // private baseUrl = import.meta.env.VITE_API_URL + '/api/admin';

  // Auth
  async login(email: string, password: string): Promise<LoginResponse> {
    try {
      const response = await adminApi.post(`/admin/signin`, {
        email,
        password,
      });

      if (!response.data) {
        throw new Error("Invalid response from server");
      }

      if (response.data.mustChangePassword) {
        return {
          mustChangePassword: true,
          resetEmailSent: response.data.resetEmailSent,
        };
      }

      return {
        id: response.data.id,
        email: response.data.email,
        password: response.data.password,
        firstName: response.data.firstName,
        lastName: response.data.lastName,
        role: response.data.role,
        isActive: response.data.isActive,
        createdAt: response.data.createdAt,
        updatedAt: response.data.updatedAt,
        accessToken: response.data.accessToken,
        refreshToken: response.data.refreshToken,
      };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          throw new Error("Invalid email or password");
        }
        throw new Error(error.response?.data?.message || "Login failed");
      }
      throw new Error("An unexpected error occurred");
    }
  }

  // Dashboard
  /** Live MRR. Cached server-side for 6h; refresh re-queries every shop on Shopify. */
  async getMrr(refresh = false): Promise<AdminMrrResponse> {
    try {
      const response = await adminApi.get(
        `/analytics/admin/mrr${refresh ? "?refresh=true" : ""}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch MRR"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async getDashboardStats(timeframe: string = "30"): Promise<DashboardStats> {
    try {
      const response = await adminApi.get(
        `/analytics/admin/overview?timeframe=${timeframe}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch dashboard stats"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  // Users
  async getUsers(
    page: number = 1,
    limit: number = 10
  ): Promise<PaginatedResponse<AdminUser>> {
    try {
      const response = await adminApi.get(`/users`, {
        params: { page, limit },
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch users"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async updateUserStatus(userId: string, isActive: boolean): Promise<void> {
    try {
      await adminApi.patch(`/users/${userId}/status`, { isActive });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to update user status"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  // Payouts
  async getPayouts(
    page: number = 1,
    limit: number = 10
  ): Promise<PaginatedResponse<AdminPayout>> {
    try {
      const response = await adminApi.get(`/payouts`, {
        params: { page, limit },
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch payouts"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async updatePayoutStatus(
    payoutId: string,
    status: "PENDING" | "COMPLETED" | "FAILED"
  ): Promise<void> {
    try {
      await adminApi.patch(`/payouts/${payoutId}/status`, { status });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to update payout status"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  // Processing Payouts
  async getProcessingPayouts(
    page: number = 1,
    timestamp?: number
  ): Promise<ProcessingPayoutsResponse> {
    try {
      const response = await adminApi.get(`/payout/processing`, {
        params: {
          page,
          _t: timestamp, // Cache-busting parameter
        },
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
      });
      return response.data;
    } catch (error) {
      console.error("Failed to fetch processing payouts:", error);
      throw error;
    }
  }

  async updateProcessingPayoutStatus(
    payoutId: string,
    status: "PROCESSING" | "COMPLETED" | "FAILED"
  ): Promise<void> {
    try {
      await adminApi.patch(`/payout/${payoutId}/status`, { status });
    } catch (error) {
      console.error("Failed to update payout status:", error);
      throw error;
    }
  }

  async processPayPalBatchPayout(
    payoutIds: string[]
  ): Promise<PayPalBatchPayoutResponse> {
    try {
      // First get the payouts to process
      const payouts = await this.getProcessingPayouts(1);
      const selectedPayouts = payouts.data.filter((payout) =>
        payoutIds.includes(payout.id)
      );

      // Prepare PayPal batch payout payload
      const payoutItems: PayPalPayoutItem[] = selectedPayouts.map((payout) => {
        const recipientType: "EMAIL" | "PHONE" | "PAYPAL_ID" = "EMAIL";
        const email =
          payout.affiliate?.email ||
          (payout.paymentDetails as { email?: string })?.email;
        if (!email) {
          throw new Error(`No email found for payout ${payout.id}`);
        }
        // Format amount to exactly 2 decimal places
        const formattedAmount = Number(payout.amount).toFixed(2);
        const item: PayPalPayoutItem = {
          recipient_type: recipientType,
          amount: {
            value: formattedAmount,
            currency: "USD",
          },
          note: `Payout for ${payout.affiliate.firstName} ${payout.affiliate.lastName}`,
          sender_item_id: payout.id,
          receiver: email,
        };
        return item;
      });

      const batchPayload = {
        sender_batch_id: `BATCH_${Date.now()}`,
        email_subject: "Your payout has been processed",
        email_message:
          "Your payout has been processed successfully. Thank you for your partnership!",
        items: payoutItems,
      };

      // Send batch payout through PayPal
      const paypalResponse: PayPalResponse = await sendBatchPayout(
        batchPayload
      );

      // Update payout statuses in our system
      await Promise.all(
        selectedPayouts.map((payout) =>
          this.updateProcessingPayoutStatus(payout.id, "COMPLETED")
        )
      );

      return {
        batchId: paypalResponse.batch_header.payout_batch_id,
        status: paypalResponse.batch_header.batch_status,
        payoutItems: paypalResponse.items.map((item: PayPalBatchItem) => ({
          payoutItemId: item.payout_item_id,
          transactionId: item.transaction_id,
          transactionStatus: item.transaction_status,
          payoutItemFee: {
            currency: item.payout_item_fee.currency,
            value: item.payout_item_fee.value,
          },
          payoutBatchId: paypalResponse.batch_header.payout_batch_id,
          payoutItem: {
            recipientType: item.payout_item.recipient_type,
            amount: {
              currency: item.payout_item.amount.currency,
              value: item.payout_item.amount.value,
            },
            note: item.payout_item.note,
            receiver: item.payout_item.receiver,
            senderItemId: item.payout_item.sender_item_id,
          },
        })),
      };
    } catch (error) {
      console.error("Failed to process PayPal batch payout:", error);
      throw error;
    }
  }

  // Support Tickets
  async getSupportTickets(
    page: number = 1,
    limit: number = 10
  ): Promise<SupportTicketsResponse> {
    try {
      const response = await adminApi.get(`/support/find-all`, {
        params: { page, limit },
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch support tickets"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async getSupportTicketById(id: string): Promise<SupportTicket> {
    try {
      const response = await adminApi.get(`/support/find-one/${id}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch support ticket"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async getMonthlyGrowth(
    year: number,
    month: number
  ): Promise<MonthlyGrowthResponse> {
    try {
      const response = await adminApi.get(
        `/analytics/monthly-growth?year=${year}&month=${month}`
      );
      return response.data;
    } catch (error) {
      console.error("Failed to fetch monthly growth data:", error);
      throw error;
    }
  }

  async toggleBrandEmailAutomation(brandId: string, enabled: boolean): Promise<any> {
    try {
      const response = await adminApi.patch(`/admin/brands/${brandId}/email-automation`, {
        enabled,
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to toggle email automation"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  // Overrides a brand's plan-derived white-label tier. Pass null to hand the
  // brand back to whatever its subscription grants; "NONE" | "BASIC" | "FULL"
  // to force a level for pilots, legacy plans or abuse. Never clears the
  // brand's saved portal name or logo.
  async setBrandWhiteLabelTier(
    brandId: string,
    tier: "NONE" | "BASIC" | "FULL" | null
  ): Promise<any> {
    try {
      const response = await adminApi.patch(`/admin/brands/${brandId}/white-label`, {
        tier,
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to update white-label access"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  // Preview of the post-checkout affiliates that would be filed under the
  // brand's portal (see convertBrandPostCheckoutAffiliates).
  async previewBrandPortalConversion(brandId: string): Promise<{
    brandDetailId: string;
    live: boolean;
    count: number;
    sample: string[];
  }> {
    try {
      const response = await adminApi.get(
        `/admin/brands/${brandId}/white-label/convertible-affiliates`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to load conversion preview"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  // Files the brand's pre-activation post-checkout affiliates — accounts
  // that only exist because of this brand and never finished signing up — as
  // recruited through its portal. Existing marketplace affiliates are never
  // touched: origin is a historical fact, not something a plan can rewrite.
  async convertBrandPostCheckoutAffiliates(
    brandId: string
  ): Promise<{ brandDetailId: string; converted: number }> {
    try {
      const response = await adminApi.post(
        `/admin/brands/${brandId}/white-label/convert-affiliates`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to convert affiliates"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  // Records a brand's verified From address for branded emails. The address's
  // domain must already be verified (sender signature + DKIM) in Postmark —
  // this only stores the outcome of that ops work. Pass null to revert the
  // brand to the platform's white-label sender.
  async setBrandWhiteLabelEmailSender(
    brandId: string,
    emailFromAddress: string | null
  ): Promise<any> {
    try {
      const response = await adminApi.patch(
        `/admin/brands/${brandId}/white-label/email-sender`,
        { emailFromAddress }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to update email sender"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  // Sales Team
  async getSalesTeam(): Promise<SalesUser[]> {
    try {
      const response = await adminApi.get(`/admin/sales-team`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || "Failed to fetch sales team");
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async createSalesUser(data: CreateSalesUserInput): Promise<{ user: SalesUser; tempPassword: string }> {
    try {
      const response = await adminApi.post(`/admin/sales-team`, data);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || "Failed to create sales user");
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async updateSalesUser(id: string, data: UpdateSalesUserInput): Promise<SalesUser> {
    try {
      const response = await adminApi.patch(`/admin/sales-team/${id}`, data);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || "Failed to update sales user");
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async deleteSalesUser(id: string): Promise<void> {
    try {
      await adminApi.delete(`/admin/sales-team/${id}`);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || "Failed to delete sales user");
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async resetSalesUserPassword(id: string): Promise<{ tempPassword: string }> {
    try {
      const response = await adminApi.post(`/admin/sales-team/${id}/reset-password`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || "Failed to reset password");
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async salesResetPassword(token: string, newPassword: string): Promise<void> {
    try {
      await api.post(`/admin/sales-reset-password`, { token, newPassword });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || "Failed to reset password");
      }
      throw new Error("An unexpected error occurred");
    }
  }
}

export const adminService = new AdminService();
